import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';

const LeadForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const getDefaultFollowupDate = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };

  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    status: 'New',
    priority: 'Medium',
    source: 'Manual',
    value: '',
    notes: '',
    ownerId: '',
    nextFollowupAt: getDefaultFollowupDate(),
    addToCalendar: false
  });

  useEffect(() => {
    fetchUsers();
    if (id) fetchLead();
  }, [id]);

  const fetchUsers = async () => {
    try {
      // Try to fetch users first, fallback to members
      let userData = [];
      try {
        const { data } = await axios.get('/api/users');
        userData = data.users || data || [];
      } catch (userError) {
        // Fallback to members if users endpoint doesn't exist
        const { data } = await axios.get('/api/members');
        userData = (data.members || data || []).map(member => ({
          id: member.id,
          firstName: member.firstName || member.name?.split(' ')[0] || member.name,
          lastName: member.lastName || member.name?.split(' ').slice(1).join(' ') || '',
          email: member.email
        }));
      }
      setUsers(userData);
    } catch (error) {
    }
  };

  const fetchLead = async () => {
    try {
      const { data } = await axios.get(`/api/crm/leads/${id}`);
      setFormData({
        name: data.name,
        company: data.company || '',
        email: data.email || '',
        phone: data.phone || '',
        status: data.status,
        priority: data.priority || 'Medium',
        source: data.source || 'Manual',
        value: data.value || '',
        notes: data.notes || '',
        ownerId: data.ownerId || '',
        nextFollowupAt: data.nextFollowupAt ? new Date(data.nextFollowupAt).toISOString().slice(0, 16) : '',
        addToCalendar: false
      });
    } catch (error) {
      toast.error('Failed to fetch lead');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const submitData = { ...formData };
      if (submitData.nextFollowupAt) {
        submitData.nextFollowupAt = new Date(submitData.nextFollowupAt).toISOString();
      } else {
        delete submitData.nextFollowupAt;
      }
      // Ensure ownerId is properly handled
      if (!submitData.ownerId || submitData.ownerId === '' || submitData.ownerId === 'null') {
        delete submitData.ownerId;
      } else {
        submitData.ownerId = parseInt(submitData.ownerId);
      }
      if (!submitData.value || submitData.value === '') {
        delete submitData.value;
      }
      
      if (id) {
        await axios.put(`/api/crm/leads/${id}`, submitData);
        toast.success('Lead updated');
      } else {
        await axios.post('/api/crm/leads', submitData);
        toast.success('Lead created');
      }
      navigate('/crm/leads');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save lead');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button
          type="button"
          onClick={() => navigate('/crm/leads')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Leads
        </button>
        <h1 className="text-3xl font-bold text-gray-900">{id ? 'Edit Lead' : 'New Lead'}</h1>
      </div>
      
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:outline-none transition-colors"
              placeholder="Enter full name"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Company</label>
            <input
              type="text"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:outline-none transition-colors"
              placeholder="Company name"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:outline-none transition-colors"
              placeholder="email@example.com"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:outline-none transition-colors"
              placeholder="+971 512345678"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:outline-none transition-colors"
            >
              <option value="New">New</option>
              <option value="Contacted">Contacted</option>
              <option value="Qualified">Qualified</option>
              <option value="Lost">Lost</option>
              <option value="Converted">Converted</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Priority</label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:outline-none transition-colors"
            >
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Source</label>
            <select
              value={formData.source}
              onChange={(e) => setFormData({ ...formData, source: e.target.value })}
              className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:outline-none transition-colors"
            >
              <option value="Manual">Manual Entry</option>
              <option value="Website">Website</option>
              <option value="Referral">Referral</option>
              <option value="Social Media">Social Media</option>
              <option value="Email Campaign">Email Campaign</option>
              <option value="Cold Call">Cold Call</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Lead Value ($)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={formData.value}
              onChange={(e) => setFormData({ ...formData, value: e.target.value })}
              className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:outline-none transition-colors"
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Owner</label>
            <select
              value={formData.ownerId}
              onChange={(e) => setFormData({ ...formData, ownerId: e.target.value })}
              className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:outline-none transition-colors"
            >
              <option value="">Select Owner</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>{user.firstName} {user.lastName}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows="4"
            className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:outline-none transition-colors"
            placeholder="Add any additional notes about this lead..."
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Next Follow-up Date</label>
          <input
            type="datetime-local"
            value={formData.nextFollowupAt}
            min={new Date().toISOString().slice(0, 16)}
            onChange={(e) => setFormData({ ...formData, nextFollowupAt: e.target.value })}
            onBlur={(e) => e.target.blur()}
            className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:outline-none transition-colors"
          />
        </div>

        {formData.nextFollowupAt && (
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="addToCalendar"
                checked={formData.addToCalendar}
                onChange={(e) => setFormData({ ...formData, addToCalendar: e.target.checked })}
                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
              />
              <label htmlFor="addToCalendar" className="text-sm font-medium text-blue-800 cursor-pointer">
                📅 Add follow-up reminder to calendar
              </label>
            </div>
          </div>
        )}

        <div className="flex gap-4 pt-6 border-t border-gray-200">
          <button
            type="submit"
            disabled={loading}
            className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              <>
                💾 {id ? 'Update Lead' : 'Save Lead'}
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate('/crm/leads')}
            className="bg-gray-100 text-gray-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default LeadForm;
